import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtGNF } from "@/lib/format";
import { Panel } from "./AdminLayout";

const COLORS = ["#0D5C3A", "#D97706", "#0284c7", "#7c3aed", "#e11d48", "#0f766e", "#b45309", "#1d4ed8"];
const tooltipStyle = { background: "#0f172a", border: "1px solid #283548", borderRadius: 12, fontSize: 12 };

const Kpi = ({ label, value, hint }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4" data-testid={`analytics-kpi-${label}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
    <p className="font-num mt-2 text-lg font-bold text-white">{value}</p>
    {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
  </div>
);

export default function AdminAnalytics() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!d) return <p className="text-sm text-slate-400" data-testid="analytics-loading">Chargement des analyses…</p>;

  const k = d.kpi;

  return (
    <div className="space-y-4" data-testid="admin-analytics-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-white">Analyses & croissance</h1>
        <p className="text-xs text-slate-400">Ventes mensuelles, marge par catégorie et classement des vendeurs</p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Chiffre d'affaires" value={fmtGNF(k.ca_total)} hint="Commandes livrées" />
        <Kpi label="Marge totale" value={fmtGNF(k.marge_totale)} />
        <Kpi label="CA 90 jours" value={fmtGNF(k.ca_90j)} hint={`${k.commandes_90j} commande(s)`} />
        <Kpi label="Panier moyen" value={fmtGNF(k.panier_moyen)} />
        <Kpi label="Taux de livraison" value={`${k.taux_livraison.toFixed(1)} %`} />
        <Kpi label="Taux d'annulation" value={`${k.taux_annulation.toFixed(1)} %`} />
        <Kpi label="Stock (prix d'achat)" value={fmtGNF(k.valeur_stock_achat)} />
        <Kpi label="Stock (prix de vente)" value={fmtGNF(k.valeur_stock_vente)} />
      </div>

      <Panel title="Ventes mensuelles" subtitle="Chiffre d'affaires et marge par mois" testid="analytics-monthly">
        {d.monthly.length === 0 ? (
          <p className="text-sm text-slate-500">Aucune donnée mensuelle.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.monthly}>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="mois" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtGNF(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="ca" name="Chiffre d'affaires" fill="#0D5C3A" radius={[6, 6, 0, 0]} />
              <Bar dataKey="marge" name="Marge" fill="#D97706" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Ventes hebdomadaires" subtitle="12 dernières semaines livrées" testid="analytics-weekly">
          {d.weekly.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune vente livrée.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={d.weekly}>
                <CartesianGrid stroke="#1e293b" vertical={false} />
                <XAxis dataKey="semaine" stroke="#64748b" fontSize={10} />
                <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtGNF(v)} />
                <Line type="monotone" dataKey="ca" name="CA" stroke="#34d399" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Marge par catégorie" subtitle="Chiffre d'affaires et marge des ventes livrées" testid="analytics-category">
          {d.by_category.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune vente livrée.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.by_category} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid stroke="#1e293b" horizontal={false} />
                <XAxis type="number" stroke="#64748b" fontSize={10} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <YAxis type="category" dataKey="categorie" stroke="#64748b" fontSize={10} width={110} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtGNF(v)} />
                <Bar dataKey="marge" name="Marge" radius={[0, 6, 6, 0]}>
                  {d.by_category.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      <Panel title="Classement des vendeurs" subtitle="Ventes et commissions générées" testid="analytics-vendors">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-left text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-400">
              <tr className="border-b border-slate-800">
                <th className="py-2">#</th>
                <th className="py-2">Vendeur</th>
                <th className="py-2 text-right">Articles vendus</th>
                <th className="py-2 text-right">Ventes</th>
                <th className="py-2 text-right">Commissions EMPIRE-SHOP</th>
              </tr>
            </thead>
            <tbody>
              {d.vendors.length === 0 && (
                <tr><td colSpan={5} className="py-4 text-sm text-slate-500">Aucune vente vendeur.</td></tr>
              )}
              {d.vendors.map((v, i) => (
                <tr key={v.vendeur} className="border-b border-slate-800/60" data-testid={`analytics-vendor-row-${i}`}>
                  <td className="font-num py-2 text-gold">{i + 1}</td>
                  <td className="py-2 text-white">{v.vendeur}</td>
                  <td className="font-num py-2 text-right text-slate-300">{v.quantite}</td>
                  <td className="font-num py-2 text-right text-emerald-400">{fmtGNF(v.ventes)}</td>
                  <td className="font-num py-2 text-right text-gold">{fmtGNF(v.commissions)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Produits les plus vendus" subtitle="Quantité, chiffre d'affaires et marge" testid="analytics-top-products">
          <div className="space-y-1.5">
            {d.top_products.length === 0 && <p className="text-sm text-slate-500">Aucune vente.</p>}
            {d.top_products.map((p) => (
              <div key={p.sku} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 p-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate text-white">{p.produit}</span>
                <span className="font-num text-slate-300">{p.quantite} u.</span>
                <span className="font-num text-emerald-400">{fmtGNF(p.ca)}</span>
                <span className="font-num text-gold">marge {fmtGNF(p.marge)}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Produits peu vendus" subtitle="Aucune vente livrée enregistrée" testid="analytics-low-rotation">
          <div className="space-y-1.5">
            {d.low_rotation.length === 0 && <p className="text-sm text-slate-500">Tous les produits ont déjà été vendus.</p>}
            {d.low_rotation.map((p) => (
              <div key={p.sku} className="flex items-center gap-3 rounded-xl border border-slate-800 p-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate text-white">{p.produit}</span>
                <span className="font-num text-slate-400">{p.sku}</span>
                <span className="font-num text-amber-300">stock {p.stock}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
